import type { CharacterSheet, JournalEntry, JournalQuestData, MapOwner, TimelineEvent } from '@roll1/shared';
import { isTimelineEventVisibleTo } from '@roll1/shared';
import { isSheetOwner } from '@roll1/shared/sheet-permissions.js';
import type { SrdSearchIndex } from '@roll1/shared/srd/types';
import { formatCr } from '@roll1/shared/srd/mapping';
import { ruMonsterAliases } from '@roll1/shared/srd/ru-names';
import type { TranslationKeys } from '../../i18n/types.js';
import { beatDisplayName, entryPlace, mapBeatId, type OwnerLookup } from '../studio/ownerPlace';
import { eventTitle } from '../chronicle/panel/rows';
import type { SearchAction, SearchEntry } from './SearchPalette';

/**
 * What the app-wide search lists, from what the client already holds — never a new request for campaign data.
 *
 * Rights (group M, point 4): the server already sends a player only what is theirs to see (journal without GM
 * entries, hidden quests, other players' personal notes and GM fields; only the players' map; no beats; published
 * chronicle events and their own drafts; sheets by the permission). This filters AGAIN by the same rules on top, so a
 * stale or wider copy in a store can never become searchable: a player's search has no beats, only the players'
 * map, only their own sheets, only entries a player may read, only events visible to them. The SRD is reference data.
 */

export type SrdKind = 'monster' | 'spell' | 'condition' | 'equipment' | 'magic-item';
export type SrdMonsterRow = SrdSearchIndex['monsters'][number];

export interface SearchViewer {
  readonly isGM: boolean;
  readonly participantId: string;
  readonly name: string;
  readonly userId?: string;
}

/** What the search needs of a beat (the Studio's own beat type and the shared one both fit). */
export interface SearchBeat {
  readonly id: string;
  readonly order: number;
  readonly name?: string | null;
  readonly sessionLabel?: string | null;
}

export interface SearchPage {
  readonly id: string;
  readonly name?: string;
  readonly owner?: MapOwner | null;
}

export interface SearchSourceInput {
  readonly t: (key: keyof TranslationKeys) => string;
  readonly locale: string;
  readonly viewer: SearchViewer;
  readonly entries: readonly JournalEntry[];
  readonly pages: readonly SearchPage[];
  readonly beats: readonly SearchBeat[];
  readonly sheets: readonly CharacterSheet[];
  readonly timeline: readonly TimelineEvent[];
  readonly srd: SrdSearchIndex | null;
  /** Lifted to the top of their tier: the running/selected beat, the map open here, the players' map. */
  readonly context: {
    readonly beatIds: readonly string[];
    readonly openPageId?: string;
    readonly playersPageId: string;
  };
  readonly open: {
    readonly journal: (id: string) => void;
    readonly page: (id: string) => void;
    readonly beat?: (id: string) => void;
    readonly sheet: (id: string) => void;
    readonly timeline: (id: string) => void;
    readonly srd: (kind: SrdKind, slug: string) => void;
  };
  /** The secondary actions of a row («Показать игрокам…», «На карту», «В бит», «Открыть рядом»). */
  readonly actionsFor?: {
    readonly journal?: (entry: JournalEntry) => readonly SearchAction[];
    readonly page?: (page: SearchPage) => readonly SearchAction[];
    readonly sheet?: (sheet: CharacterSheet) => readonly SearchAction[];
    readonly monster?: (monster: SrdMonsterRow) => readonly SearchAction[];
  };
  /** The commands of the place (create, switch, start a session) — the host's own, already role-checked. */
  readonly commands: readonly SearchEntry[];
}

/** A journal entry a player may read: the server's rule (filterJournalForRole), applied again on the client copy. */
export function playerMayReadEntry(entry: JournalEntry, participantId: string): boolean {
  if (entry.visibility === 'gm') return false;
  if (entry.visibility === 'personal') return entry.createdBy === participantId;
  if (entry.type === 'quest' && (entry.data as JournalQuestData).status === 'hidden') return false;
  return true;
}

/** Index only reader-visible prose, even when handed a wider/stale client copy. */
export function journalSearchText(entry: JournalEntry, gm: boolean): string {
  const data = (entry.data ?? {}) as unknown as Record<string, unknown>;
  const fields = ['description', 'content', 'occupation', 'race', 'attitude', 'category', 'rewards', 'objectives', ...(gm ? ['gmNotes', 'secrets', 'personality', 'questHooks'] : [])];
  return fields.flatMap(key => {
    if (!gm && entry.revealedFields && !entry.revealedFields.includes(key)) return [];
    const value = data[key];
    if (typeof value === 'string') return [value];
    if (key === 'questHooks' && Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
    if (key === 'objectives' && entry.type === 'quest') return ((entry.data as JournalQuestData).objectives ?? []).filter(o => gm || !o.hidden).map(o => `${o.text}\n${o.description ?? ''}`);
    return [];
  }).join('\n');
}


export function buildSearchEntries(input: SearchSourceInput): SearchEntry[] {
  const { t, viewer, context, open, actionsFor } = input;
  const gm = viewer.isGM;
  const fill = (key: keyof TranslationKeys, name: string): string => t(key).replace('{name}', name);
  const out: SearchEntry[] = [...input.commands];

  // ── Beats (GM only: the preparation) ──
  const beats: readonly SearchBeat[] = gm ? input.beats : [];
  const beatName = (beatId: string): string => {
    const beat = beats.find((b) => b.id === beatId);
    return beat === undefined ? '' : beatDisplayName(beat, t);
  };
  if (open.beat !== undefined) {
    const openBeat = open.beat;
    for (const beat of beats) {
      const running = context.beatIds[0] === beat.id;
      out.push({
        id: beat.id,
        kind: 'beat',
        label: beatDisplayName(beat, t),
        hint: running ? t('search.hint.current') : undefined,
        boost: context.beatIds.includes(beat.id) ? 1 : 0,
        run: () => openBeat(beat.id),
      });
    }
  }

  // ── Journal entries (nested in beats, quests and objectives included, with their place) ──
  const entries = gm ? input.entries : input.entries.filter((e) => playerMayReadEntry(e, viewer.participantId));
  const lookup: OwnerLookup = {
    beatExists: (beatId) => beats.some((b) => b.id === beatId),
    quest: (questId) => {
      const quest = entries.find((e) => e.id === questId && e.type === 'quest');
      return quest === undefined ? undefined : { objectives: ((quest.data as JournalQuestData).objectives ?? []).map((o) => ({ id: o.id, text: o.text })) };
    },
  };
  const questName = (questId: string): string => entries.find((e) => e.id === questId)?.name ?? '';
  for (const entry of entries) {
    // The owner is the GM's organisation: a player's copy has none (stripGmFields); a player never sees a place.
    const place = gm ? entryPlace(entry.owner, lookup) : null;
    const hint =
      place === null
        ? t(`journal.type.${entry.type}` as keyof TranslationKeys)
        : place.kind === 'beat'
          ? fill('studio.owner.inBeat', beatName(place.beatId))
          : place.kind === 'objective'
            ? fill('studio.owner.inObjective', place.objectiveText)
            : fill('studio.owner.inQuest', questName(place.questId));
    const inContext = place !== null && place.kind === 'beat' && context.beatIds.includes(place.beatId);
    out.push({
      id: entry.id,
      kind: 'journal',
      label: entry.name,
      names: entry.tags ?? [],
      content: journalSearchText(entry, gm),
      hint,
      boost: inContext ? 0.8 : 0,
      run: () => open.journal(entry.id),
      actions: actionsFor?.journal?.(entry),
    });
  }

  // ── Maps: the GM's all (nested in beats with their beat); a player only the players' map ──
  const pages = gm ? input.pages : input.pages.filter((p) => p.id === context.playersPageId);
  for (const page of pages) {
    const beatId = gm ? mapBeatId(page.owner ?? undefined, lookup) : null;
    const open_ = page.id === context.openPageId;
    const players = page.id === context.playersPageId;
    const hints: string[] = [];
    if (beatId !== null) hints.push(fill('studio.owner.inBeat', beatName(beatId)));
    if (gm && players) hints.push(t('search.hint.players'));
    if (gm && open_ && !players) hints.push(t('search.hint.open'));
    out.push({
      id: page.id,
      kind: 'page',
      label: page.name ?? page.id,
      hint: hints.join(' · ') || undefined,
      boost: open_ ? 1 : players ? 0.5 : beatId !== null && context.beatIds.includes(beatId) ? 0.8 : 0,
      run: () => open.page(page.id),
      actions: actionsFor?.page?.(page),
    });
  }

  // ── Sheets: the GM's all; a player only their own ──
  const sheets = gm ? input.sheets : input.sheets.filter((s) => isSheetOwner(s, viewer.name, viewer.userId));
  for (const sheet of sheets) {
    const beatId = gm ? mapBeatId(sheet.holder ?? undefined, lookup) : null;
    out.push({
      id: sheet.id,
      kind: 'sheet',
      label: sheet.name,
      hint: beatId !== null ? fill('studio.owner.inBeat', beatName(beatId)) : sheet.sheetType === 'npc' ? t('search.hint.npc') : undefined,
      boost: beatId !== null && context.beatIds.includes(beatId) ? 0.8 : 0,
      run: () => open.sheet(sheet.id),
      actions: actionsFor?.sheet?.(sheet),
    });
  }

  // ── The chronicle: what this person may see of it ──
  const events = gm ? input.timeline : input.timeline.filter((e) => isTimelineEventVisibleTo(e, viewer.participantId, 'player'));
  for (const event of events) {
    out.push({
      id: event.id,
      kind: 'timeline',
      label: eventTitle(event, t),
      run: () => open.timeline(event.id),
    });
  }

  // ── The SRD: reference data, the same for everyone ──
  const srd = input.srd;
  if (srd !== null) {
    const ru = input.locale === 'ru';
    const labelOf = (row: { name: string; ru?: string }): { label: string; names: string[]; hint?: string } =>
      ru && row.ru !== undefined ? { label: row.ru, names: [row.name], hint: row.name } : { label: row.name, names: row.ru === undefined ? [] : [row.ru] };
    for (const m of srd.monsters) {
      const l = labelOf(m);
      out.push({
        id: m.slug, kind: 'monster', label: l.label, names: [...l.names, ...ruMonsterAliases(m.name)], searchOnly: true,
        hint: `${l.hint === undefined ? '' : `${l.hint} · `}CR ${formatCr(m.cr)}`,
        run: () => open.srd('monster', m.slug),
        actions: actionsFor?.monster?.(m),
      });
    }
    for (const s of srd.spells) {
      const l = labelOf(s);
      const level = s.level === 0 ? t('search.hint.cantrip') : t('search.hint.level').replace('{n}', String(s.level));
      out.push({ id: s.slug, kind: 'spell', label: l.label, names: l.names, searchOnly: true, hint: `${l.hint === undefined ? '' : `${l.hint} · `}${level}`, run: () => open.srd('spell', s.slug) });
    }
    for (const c of srd.conditions) {
      const l = labelOf(c);
      out.push({ id: c.slug, kind: 'condition', label: l.label, names: l.names, searchOnly: true, hint: l.hint, run: () => open.srd('condition', c.slug) });
    }
    for (const e of srd.equipment) {
      const l = labelOf(e);
      out.push({ id: `eq:${e.slug}`, kind: 'item', label: l.label, names: l.names, searchOnly: true, hint: l.hint ?? e.category, run: () => open.srd('equipment', e.slug) });
    }
    for (const i of srd.magicItems) {
      out.push({ id: `mi:${i.slug}`, kind: 'item', label: i.name, searchOnly: true, hint: i.rarity, run: () => open.srd('magic-item', i.slug) });
    }
  }
  return out;
}
